// ============================================================================
// Dashboard data hook — single source of truth for the six executive widgets.
//
// Why one hook for all widgets (instead of per-widget hooks):
//   * Six widgets fired in parallel from one place avoids the staircase of
//     spinners you get when each widget owns its own request.
//   * A unified `lastUpdated` timestamp powers the global "Refreshed at HH:MM"
//     stamp + the auto-refresh logic below.
//   * Cancellation is one flag, not six.
//
// Auto-refresh: every 5 minutes via setInterval. setVisible refresh kicks in
// when the tab regains focus so a Bryan/Ben morning open never shows stale
// values from the prior day.
//
// Data shaping notes (from live-DB verification, May 2026):
//   * `opportunities.expected_arr` / `expected_mrr` / `bid_amount` are NULL
//     across all rows. Pipeline widget shows counts + stage_detail buckets,
//     not $-weighted values, until those columns get backfilled.
//   * `hiring_requests` table is empty. Alerts widget treats 0 open hires as
//     a real value (not a placeholder) so the row simply reads "0 open".
//   * `account_status_rag` has 4 NULL rows. Facility Health renders them as
//     a fourth "Unset" slice rather than silently dropping them.
//   * Revenue trend is derived from facilities.contract_start (real signal:
//     MRR added per month from new facilities going live). The cached
//     denormalised counters are not maintained, so we don't trust them.
// ============================================================================

import { useEffect, useState } from 'react';
import { supabase } from '../../shared/supabase';
import type {
  FacilityFeedback,
  FeedbackCategory,
  FeedbackSentiment,
  FeedbackStatus,
  RagStatus,
} from '../../shared/types';
import { errorMessage } from '../../shared/errors';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// ----- Aggregate types ------------------------------------------------------

export interface FacilityHealthBuckets {
  green: number;
  amber: number;
  red: number;
  unset: number;
  total: number;
}

export interface RevenueSnapshot {
  // Total of monthly_rate across all Active facilities with a populated rate.
  current_mrr: number;
  // 12 * current_mrr — quick-glance ARR for board context.
  current_arr: number;
  // Number of Active facilities contributing to MRR (those with a rate).
  paying_facilities: number;
  // Active facilities in the dataset that are missing a monthly_rate. Surfaced
  // as a footnote so leadership knows the MRR is a floor, not a ceiling.
  missing_rate_count: number;
  // Last 13 months of newly-onboarded MRR (drawn from contract_start). 13 so
  // the chart shows the most recent full year *plus* the current partial month
  // for context.
  mrr_added_by_month: { month: string; mrr_added: number; count: number }[];
}

export interface AccountHealthSnapshot {
  active: number;
  flagged: number;
  closed: number;
  total: number;
  recent_issues: RecentIssue[];
}

export interface RecentIssue {
  feedback_id: string;
  facility_id: string;
  facility_name: string | null;
  account_name: string | null;
  feedback_date: string;
  category: FeedbackCategory;
  description: string;
  status: FeedbackStatus;
  sentiment: FeedbackSentiment;
}

export interface OpportunityBucket {
  stage: string;
  stage_detail: string | null;
  count: number;
}

export interface OperationalAlerts {
  red_facilities: number;
  amber_facilities: number;
  open_feedback: number;
  recurring_feedback: number;
  feedback_last_7d: number;
  hiring_requests_open: number;
  facilities_missing_rag: number;
  facilities_missing_geo: number;
}

export interface GeographicBucket {
  market: string;
  count: number;
  mrr: number;
}

// ----- Portfolio aggregates (Bryan's CEO portfolio view, added 2026-05-12) --

// Top-line KPIs across the active portfolio. Drives the four-card scan row
// at the top of Bryan's view. Margin here is weighted by ARR — small accounts
// shouldn't drag the headline number down disproportionately.
export interface PortfolioSummary {
  total_arr: number;
  weighted_margin_pct: number; // 0..1 fraction (e.g. 0.265 for 26.5%)
  total_annual_margin: number; // total_arr * weighted_margin_pct, precomputed
  active_site_count: number;
  states_served_count: number; // distinct states across ASC + Dialysis only
  margin_observed_count: number; // # of active facilities where margin could be computed (both rates present)
  margin_data_quality_excluded_count: number; // # active facilities excluded from margin views: zero assignments OR billing=0 OR sub=0
}

// Histogram data for the margin distribution widget. 2.5pp bins from 0-50%.
// The widget colours bars by band: red <15%, blue 15-25%, green 25-40%,
// yellow >=40%. Anything < 0 or > 50% goes into the edge bins so we don't
// drop data silently.
export interface MarginBucket {
  bin_lo: number; // fraction, inclusive
  bin_hi: number; // fraction, exclusive
  count: number;
  // Facility names + margins in this bucket so the widget can show the list
  // on hover/tap. Cap to first 10 to keep the payload small.
  examples: { facility_id: string; facility_name: string; margin: number }[];
}

// Top 5 and bottom 5 margin sites among active facilities with monthly_rate
// above a noise threshold ($500/mo). Flight-risk review for the bottom set,
// "what makes these healthy?" review for the top set.
export interface MarginExtreme {
  facility_id: string;
  facility_name: string;
  account_name: string | null;
  segment: string;
  margin_pct: number; // fraction
  monthly_rate: number;
  arr: number;
}

export interface MarginExtremes {
  lowest: MarginExtreme[];
  highest: MarginExtreme[];
}

// ----- Hook return shape ----------------------------------------------------

export interface DashboardData {
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refresh: () => void;

  facilityHealth: FacilityHealthBuckets | null;
  revenue: RevenueSnapshot | null;
  accountHealth: AccountHealthSnapshot | null;
  pipeline: OpportunityBucket[] | null;
  alerts: OperationalAlerts | null;
  geographic: GeographicBucket[] | null;
  portfolioSummary: PortfolioSummary | null;
  marginDistribution: MarginBucket[] | null;
  marginExtremes: MarginExtremes | null;
}

// ----- Internal row shapes (only the columns we actually read) --------------

interface FacilityRow {
  facility_id: string;
  facility_name: string | null;
  account_id: string | null;
  status: string | null;
  account_status_rag: RagStatus | null;
  monthly_rate: number | null;
  // 2026-08-13 (CW-K, TCSD-0031): monthly_rate_base is RETIRED and the
  // client-side GREATEST() heuristic that consumed it is gone. The hedge
  // existed in three places — v_facility_margins, v_dashboard_kpis, and here
  // — which is why the 158-facility disagreement stayed hidden for months.
  // The single definition now lives in SQL and is read below.
  effective_client_rate_monthly: number | null;
  // 2026-05-19 (afternoon fix): labor margin is now computed entirely from
  // assignment-level data on BOTH sides (client rate AND sub cost). The
  // facility-level `monthly_rate` stays as the "marketed/contracted" rate
  // for revenue rollups, but for margin we sum across active assignments:
  //   * billing_total_monthly = SUM(billing_rate) where active
  //   * sub_total_monthly     = SUM(sub_rate_monthly) where active
  //   * labor_margin          = (billing_total - sub_total) / billing_total
  // Facilities with active_assignment_count=0, billing_total=0, OR
  // sub_total=0 are excluded from margin observations (flagged as
  // data-quality, not as 100% / -281% outliers).
  active_assignment_count: number;
  billing_total_monthly: number | null;
  sub_rate_monthly: number | null;
  facility_type: string | null;
  state: string | null;
  market_region: string | null;
  latitude: number | null;
  longitude: number | null;
  contract_start: string | null;
}

interface AccountRow {
  account_id: string;
  account_name: string | null;
  account_status: string | null;
}

interface OpportunityRow {
  opportunity_id: string;
  opportunity_stage: string | null;
  stage_detail: string | null;
}

interface FeedbackRow
  extends Pick<
    FacilityFeedback,
    | 'feedback_id'
    | 'facility_id'
    | 'feedback_date'
    | 'category'
    | 'description'
    | 'status'
    | 'sentiment'
  > {}

// ----- Needs (2026-06-11, dashboard personalization) ------------------------
// Which query domains the currently-visible widgets actually consume. Hiding
// a widget skips the queries only it needed — e.g. hiding Pipeline drops the
// opportunities fetch entirely. Derived by the dashboard from the widget
// registry; defaults to everything for any caller that doesn't pass needs.

export interface DashboardNeeds {
  facilities: boolean;
  accounts: boolean;
  opportunities: boolean;
  feedback: boolean;
  assignments: boolean;
  hiring: boolean;
}

export const ALL_NEEDS: DashboardNeeds = {
  facilities: true,
  accounts: true,
  opportunities: true,
  feedback: true,
  assignments: true,
  hiring: true,
};

// ----- Hook -----------------------------------------------------------------

export function useDashboardData(
  needs: DashboardNeeds = ALL_NEEDS,
  // False while the caller is still resolving which widgets are visible
  // (prefs load). Prevents a wasted all-domains fetch on first paint that
  // would defeat the hidden-widgets-don't-fetch guarantee.
  enabled = true,
): DashboardData {
  // Stable key so the effect re-runs (and refetches) exactly when the set of
  // needed domains changes — not on every render's fresh object identity.
  const needsKey = [
    needs.facilities,
    needs.accounts,
    needs.opportunities,
    needs.feedback,
    needs.assignments,
    needs.hiring,
  ]
    .map((b) => (b ? '1' : '0'))
    .join('');
  const [data, setData] = useState<Omit<DashboardData, 'refresh'>>({
    loading: true,
    error: null,
    lastUpdated: null,
    facilityHealth: null,
    revenue: null,
    accountHealth: null,
    pipeline: null,
    alerts: null,
    geographic: null,
    portfolioSummary: null,
    marginDistribution: null,
    marginExtremes: null,
  });

  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    async function load() {
      // On manual refresh / interval tick, keep prior values visible while
      // we re-fetch (only flip `loading` true on the first load). Avoids the
      // dashboard-blanks-then-reloads flash on every interval boundary.
      setData((prev) => ({
        ...prev,
        loading: prev.lastUpdated === null,
        error: null,
      }));

      try {
        // All five queries fire in parallel. Postgrest serialises them on the
        // backend connection pool but on the wire they're concurrent — total
        // wall time tracks the slowest single query, not the sum.
        // Each query fires only when a visible widget needs its domain
        // (personalization, 2026-06-11). `null` slots short-circuit to no
        // request; their aggregates are surfaced as null below.
        const [fRes, aRes, oRes, fbRes, saRes, mRes] = await Promise.all([
          // 2026-05-12 repoint: Dashboard portfolio view is operational —
          // closed/cancelled facilities should not appear in RAG counts,
          // margin distribution, or extremes. active_facilities excludes
          // status IN ('Closed','Cancelled').
          // 2026-05-19: facilities.sub_rate_monthly dropped — fetched
          // separately via subAssignments below and merged client-side.
          needs.facilities
            ? supabase
                .from('active_facilities')
                .select(
                  'facility_id, facility_name, account_id, status, account_status_rag, monthly_rate, facility_type, state, market_region, latitude, longitude, contract_start',
                )
            : null,
          needs.accounts
            ? supabase
                .from('accounts')
                .select('account_id, account_name, account_status')
            : null,
          needs.opportunities
            ? supabase
                .from('opportunities')
                .select('opportunity_id, opportunity_stage, stage_detail')
            : null,
          needs.feedback
            ? supabase
                .from('facility_feedback')
                .select(
                  'feedback_id, facility_id, feedback_date, category, description, status, sentiment',
                )
                .in('status', ['Open', 'Recurring'])
                .order('feedback_date', { ascending: false })
                .limit(50)
            : null,
          // 2026-05-19 (afternoon fix): pull billing_rate alongside
          // sub_rate_monthly so labor margin is computed entirely from
          // assignment-level data. Otherwise the headline-MRR side and the
          // assignment side disagree on facilities like FAC-0117 (Houston
          // Eye), where the bid sheet was entered as two assignments but
          // facility.monthly_rate is still the old single-line value.
          needs.assignments
            ? supabase
                .from('service_assignments')
                .select(
                  'facility_id, sub_rate_monthly, billing_rate, assignment_status',
                )
                .eq('assignment_status', 'Active')
            : null,
          // 2026-08-13 (CW-K, TCSD-0031): the effective client rate is defined
          // ONCE, in v_facility_margins. Previously this file re-derived it with
          // Math.max(monthly_rate, monthly_rate_base, SUM(billing_rate)) — a
          // third copy of the GREATEST hedge that drifted from the SQL silently.
          // Read the definition rather than reimplementing it.
          needs.assignments
            ? supabase
                .from('v_facility_margins')
                .select('facility_id, effective_client_rate_monthly')
            : null,
        ]);

        const facilities = fRes?.data ?? null;
        const accounts = aRes?.data ?? null;
        const opportunities = oRes?.data ?? null;
        const feedback = fbRes?.data ?? null;
        const subAssignments = saRes?.data ?? null;
        const facilityMargins = mRes?.data ?? null;
        if (mRes?.error) throw mRes.error;
        if (fRes?.error) throw fRes.error;
        if (aRes?.error) throw aRes.error;
        if (oRes?.error) throw oRes.error;
        if (fbRes?.error) throw fbRes.error;
        if (saRes?.error) throw saRes.error;

        // Aggregate sub_rate_monthly + billing_rate per facility from the
        // assignments fetch, plus an active-assignment count. Graft onto
        // each FacilityRow so the labor-margin pipeline can compute from
        // assignment-level data on both sides and exclude data-quality
        // outliers (zero assignments / zero sub / zero billing).
        const subTotalByFacility = new Map<string, number>();
        const billingTotalByFacility = new Map<string, number>();
        const countByFacility = new Map<string, number>();
        for (const sa of (subAssignments ?? []) as {
          facility_id: string | null;
          sub_rate_monthly: number | null;
          billing_rate: number | null;
        }[]) {
          if (!sa.facility_id) continue;
          countByFacility.set(
            sa.facility_id,
            (countByFacility.get(sa.facility_id) ?? 0) + 1,
          );
          if (typeof sa.sub_rate_monthly === 'number') {
            subTotalByFacility.set(
              sa.facility_id,
              (subTotalByFacility.get(sa.facility_id) ?? 0) + sa.sub_rate_monthly,
            );
          }
          if (typeof sa.billing_rate === 'number') {
            billingTotalByFacility.set(
              sa.facility_id,
              (billingTotalByFacility.get(sa.facility_id) ?? 0) + sa.billing_rate,
            );
          }
        }
        // 2026-08-13 (CW-K): effective client rate comes from the view, not from
        // arithmetic here. NULL is meaningful — it means the facility is not on a
        // monthly rate (hourly hospitals, TCSD-0033) and must never render as $0.
        const effectiveRateByFacility = new Map<string, number | null>();
        for (const m of (facilityMargins ?? []) as {
          facility_id: string | null;
          effective_client_rate_monthly: number | null;
        }[]) {
          if (!m.facility_id) continue;
          effectiveRateByFacility.set(
            m.facility_id,
            typeof m.effective_client_rate_monthly === 'number'
              ? m.effective_client_rate_monthly
              : null,
          );
        }
        for (const f of (facilities ?? []) as FacilityRow[]) {
          (f as FacilityRow).sub_rate_monthly =
            subTotalByFacility.get(f.facility_id) ?? null;
          (f as FacilityRow).billing_total_monthly =
            billingTotalByFacility.get(f.facility_id) ?? null;
          (f as FacilityRow).active_assignment_count =
            countByFacility.get(f.facility_id) ?? 0;
          (f as FacilityRow).effective_client_rate_monthly =
            effectiveRateByFacility.get(f.facility_id) ?? null;
        }

        // Hiring requests is its own query because the table can be empty —
        // a head-only count avoids hauling rows we won't render. Status
        // vocabulary is 'Open' / 'Covered' / 'Not Covered' (capitalized);
        // lowercase 'open' silently returns 0. Only fetched when the Alerts
        // widget is visible (it's the sole consumer).
        let hiringOpen: number | null = null;
        if (needs.hiring) {
          const { count, error: hrErr } = await supabase
            .from('hiring_requests')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'Open');
          if (hrErr) throw hrErr;
          hiringOpen = count ?? 0;
        }

        const facilityRows = (facilities ?? []) as FacilityRow[];
        const accountRows = (accounts ?? []) as AccountRow[];
        const opportunityRows = (opportunities ?? []) as OpportunityRow[];
        const feedbackRows = (feedback ?? []) as FeedbackRow[];

        // ----- Facility Health (RAG buckets across ALL facilities) ----------
        // Spec asks for 182 facilities split Green/Amber/Red. We honour that
        // by counting every row regardless of lifecycle status — Closed and
        // Pending facilities still need RAG visibility for COO oversight.
        const facilityHealth: FacilityHealthBuckets = {
          green: 0,
          amber: 0,
          red: 0,
          unset: 0,
          total: facilityRows.length,
        };
        for (const f of facilityRows) {
          if (f.account_status_rag === 'Green') facilityHealth.green += 1;
          else if (f.account_status_rag === 'Amber') facilityHealth.amber += 1;
          else if (f.account_status_rag === 'Red') facilityHealth.red += 1;
          else facilityHealth.unset += 1;
        }

        // ----- Revenue (Active-only MRR + monthly trend from contract_start)
        const activeFacilities = facilityRows.filter(
          (f) => f.status === 'Active',
        );
        let mrr = 0;
        let paying = 0;
        let missingRate = 0;
        for (const f of activeFacilities) {
          if (typeof f.monthly_rate === 'number' && f.monthly_rate > 0) {
            mrr += f.monthly_rate;
            paying += 1;
          } else {
            missingRate += 1;
          }
        }

        // 13-month bucket: last 12 full months + current partial month.
        const now = new Date();
        const buckets: { month: string; mrr_added: number; count: number }[] =
          [];
        for (let i = 12; i >= 0; i -= 1) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          buckets.push({ month: monthKey(d), mrr_added: 0, count: 0 });
        }
        const bucketIdx: Record<string, number> = {};
        buckets.forEach((b, i) => {
          bucketIdx[b.month] = i;
        });
        for (const f of activeFacilities) {
          if (!f.contract_start) continue;
          const key = monthKey(new Date(f.contract_start));
          const idx = bucketIdx[key];
          if (idx === undefined) continue;
          buckets[idx].count += 1;
          buckets[idx].mrr_added += f.monthly_rate ?? 0;
        }
        const revenue: RevenueSnapshot = {
          current_mrr: mrr,
          current_arr: mrr * 12,
          paying_facilities: paying,
          missing_rate_count: missingRate,
          mrr_added_by_month: buckets,
        };

        // ----- Account Health (counts + 5 most recent open issues) ---------
        let activeAccounts = 0;
        let flaggedAccounts = 0;
        let closedAccounts = 0;
        for (const a of accountRows) {
          if (a.account_status === 'Active') activeAccounts += 1;
          else if (a.account_status === 'FLAG') flaggedAccounts += 1;
          else if (a.account_status === 'Closed') closedAccounts += 1;
        }

        // Build account+facility lookup so RecentIssue can render the names
        // without an extra round trip per row.
        const accountById: Record<string, AccountRow> = {};
        for (const a of accountRows) accountById[a.account_id] = a;
        const facilityById: Record<string, FacilityRow> = {};
        for (const f of facilityRows) facilityById[f.facility_id] = f;

        const recentIssues: RecentIssue[] = feedbackRows
          .slice(0, 5)
          .map((fb) => {
            const fac = facilityById[fb.facility_id];
            const acc = fac?.account_id
              ? accountById[fac.account_id]
              : undefined;
            return {
              feedback_id: fb.feedback_id,
              facility_id: fb.facility_id,
              facility_name: fac?.facility_name ?? null,
              account_name: acc?.account_name ?? null,
              feedback_date: fb.feedback_date,
              category: fb.category,
              description: fb.description,
              status: fb.status,
              sentiment: fb.sentiment,
            };
          });

        const accountHealth: AccountHealthSnapshot = {
          active: activeAccounts,
          flagged: flaggedAccounts,
          closed: closedAccounts,
          total: accountRows.length,
          recent_issues: recentIssues,
        };

        // ----- Pipeline (group by stage + stage_detail) --------------------
        // Live data: Won/Active/Pending stages × {Confirmation, Pre_Invoicing,
        // Onboarding, QBO_Setup, Executive_Review, Won}. We collapse the
        // post-close "Won" group into a single bucket since it's mostly
        // historical, and surface Active + Pending in detail since those are
        // the deals leadership actually wants visibility on.
        const pipelineMap: Record<string, OpportunityBucket> = {};
        for (const o of opportunityRows) {
          const stage = o.opportunity_stage ?? 'Unknown';
          const detail = stage === 'Won' ? null : o.stage_detail;
          const key = `${stage}|${detail ?? ''}`;
          const existing = pipelineMap[key];
          if (existing) {
            existing.count += 1;
          } else {
            pipelineMap[key] = {
              stage,
              stage_detail: detail,
              count: 1,
            };
          }
        }
        const pipeline: OpportunityBucket[] = Object.values(pipelineMap).sort(
          stageSort,
        );

        // ----- Operational Alerts (computed signals) -----------------------
        const alerts: OperationalAlerts = {
          red_facilities: facilityHealth.red,
          amber_facilities: facilityHealth.amber,
          open_feedback: feedbackRows.filter((fb) => fb.status === 'Open')
            .length,
          recurring_feedback: feedbackRows.filter(
            (fb) => fb.status === 'Recurring',
          ).length,
          feedback_last_7d: countRecentFeedback(feedbackRows, 7),
          hiring_requests_open: hiringOpen ?? 0,
          facilities_missing_rag: facilityHealth.unset,
          facilities_missing_geo: activeFacilities.filter(
            (f) => f.latitude === null || f.longitude === null,
          ).length,
        };

        // ----- Geographic (Active facilities by market_region) -------------
        const geoMap: Record<string, GeographicBucket> = {};
        for (const f of activeFacilities) {
          const key = f.market_region ?? 'Unassigned';
          if (!geoMap[key]) {
            geoMap[key] = { market: key, count: 0, mrr: 0 };
          }
          geoMap[key].count += 1;
          geoMap[key].mrr += f.monthly_rate ?? 0;
        }
        const geographic: GeographicBucket[] = Object.values(geoMap).sort(
          (a, b) => b.count - a.count,
        );

        // ----- Portfolio Summary, Margin Distribution, Margin Extremes -----
        // (Bryan's CEO portfolio view, added 2026-05-12; reworked 2026-05-19
        // afternoon to fix the "100% / -281%" widget bug.)
        //
        // LABOR MARGIN definition (per Ben):
        //   (rate paid by client − rate paid to sub) / rate paid by client
        // applied to AGREED SERVICES (i.e. what's recorded as active
        // service_assignments rows). Both sides come from service_assignments:
        //   * client rate = SUM(billing_rate)     across active assignments
        //   * sub cost    = SUM(sub_rate_monthly) across active assignments
        // facility.monthly_rate is intentionally NOT used here because it
        // can drift from the assignment-level billing — e.g. FAC-0117 had
        // monthly_rate=$2,950 vs assignment-level billing=$15,525 because
        // the second service line was added without refreshing the cached
        // value, producing the spurious -281.6% reading in the widget.
        //
        // Data-quality exclusions (don't pollute the margin views):
        //   * active_assignment_count = 0  → no assignment yet, sub unknown
        //   * billing_total_monthly  = 0   → assignment exists but billing
        //                                    rate missing
        //   * sub_total_monthly      = 0   → assignment exists but sub rate
        //                                    missing
        // Counted separately as `data_quality_excluded` so the widget can
        // flag the cleanup queue without dragging Extremes off the rails.
        //
        // ARR weighting on the KPI margin: small accounts shouldn't drag the
        // headline number. A $50/mo trial facility with 80% margin shouldn't
        // hide a $300k/yr hospital running at 18%.

        const marginObservations: {
          row: FacilityRow;
          margin: number; // fraction
          arr: number;
          billingMonthly: number; // effective client rate (GREATEST of two sources)
          subMonthly: number;
          segment: string;
        }[] = [];
        let dataQualityExcluded = 0;
        for (const f of activeFacilities) {
          const sub = f.sub_rate_monthly;
          const count = f.active_assignment_count ?? 0;
          // 2026-08-13 (CW-K, TCSD-0031). This used to be
          //   Math.max(monthly_rate, monthly_rate_base, SUM(billing_rate))
          // — a third copy of the GREATEST hedge, alongside v_facility_margins
          // and v_dashboard_kpis. "Whichever number is biggest" is a hedge, not
          // a definition: it hid a 158-facility disagreement and inflated the
          // portfolio by $63,285/mo. monthly_rate_base is retired; the single
          // definition now lives in v_facility_margins and is read, not rebuilt.
          // NULL (hourly hospitals) falls through the gate below as unmeasurable
          // rather than being coerced to 0 and read as free work.
          const effectiveClientRate =
            typeof f.effective_client_rate_monthly === 'number'
              ? f.effective_client_rate_monthly
              : 0;
          // Data-quality gate: need at least one active assignment with a
          // sub rate AND some form of client rate. Otherwise this facility's
          // margin reading is meaningless — exclude from observations.
          if (
            count === 0 ||
            typeof sub !== 'number' ||
            sub <= 0 ||
            effectiveClientRate <= 0
          ) {
            dataQualityExcluded += 1;
            continue;
          }
          const margin = (effectiveClientRate - sub) / effectiveClientRate;
          marginObservations.push({
            row: f,
            margin,
            arr: effectiveClientRate * 12,
            billingMonthly: effectiveClientRate,
            subMonthly: sub,
            segment: segmentFor(f, accountById),
          });
        }

        // KPI: states served (ASC + Dialysis only — hospitals are all KC metro,
        // including them would skew the count and isn't useful at portfolio
        // level). Use the `state` column when present, fall back to extracting
        // from market_region (e.g. "KS Metro KC" → "KS").
        const statesSet = new Set<string>();
        for (const f of activeFacilities) {
          if (f.facility_type !== 'Surgery_Center' && f.facility_type !== 'Dialysis')
            continue;
          const st = (f.state || '').trim().toUpperCase();
          if (st && st.length === 2) statesSet.add(st);
        }

        let weightedMarginNumerator = 0;
        let weightedMarginDenominator = 0;
        for (const m of marginObservations) {
          weightedMarginNumerator += m.margin * m.arr;
          weightedMarginDenominator += m.arr;
        }
        const weightedMarginPct =
          weightedMarginDenominator > 0
            ? weightedMarginNumerator / weightedMarginDenominator
            : 0;
        const totalArr = activeFacilities.reduce(
          (s, f) => s + (f.monthly_rate ?? 0) * 12,
          0,
        );
        const portfolioSummary: PortfolioSummary = {
          total_arr: totalArr,
          weighted_margin_pct: weightedMarginPct,
          total_annual_margin: totalArr * weightedMarginPct,
          active_site_count: activeFacilities.length,
          states_served_count: statesSet.size,
          margin_observed_count: marginObservations.length,
          margin_data_quality_excluded_count: dataQualityExcluded,
        };

        // Margin histogram: 20 bins of 2.5pp width from 0 to 50%. Outliers
        // (negative or > 50%) clamp to the edge bins so they're visible.
        const BIN_SIZE = 0.025;
        const BIN_COUNT = 20;
        const marginDistribution: MarginBucket[] = [];
        for (let i = 0; i < BIN_COUNT; i += 1) {
          marginDistribution.push({
            bin_lo: i * BIN_SIZE,
            bin_hi: (i + 1) * BIN_SIZE,
            count: 0,
            examples: [],
          });
        }
        for (const m of marginObservations) {
          let idx = Math.floor(m.margin / BIN_SIZE);
          if (idx < 0) idx = 0;
          if (idx >= BIN_COUNT) idx = BIN_COUNT - 1;
          const bucket = marginDistribution[idx];
          bucket.count += 1;
          if (bucket.examples.length < 10) {
            bucket.examples.push({
              facility_id: m.row.facility_id,
              facility_name:
                m.row.facility_name ?? m.row.facility_id,
              margin: m.margin,
            });
          }
        }

        // Margin extremes: top 5 and bottom 5 active facilities with
        // billing_total > $500 (assignment-level, not facility-level). The
        // $500 threshold drops trial / pass-through / edge-case rows that
        // would otherwise dominate the extremes lists. As of 2026-05-19
        // afternoon, the qualifier uses the assignment-level billing sum
        // (same source as the margin numerator) so the filter and the
        // margin computation can't disagree.
        const qualified = marginObservations.filter(
          (m) => m.billingMonthly > 500,
        );
        const byMarginAsc = [...qualified].sort((a, b) => a.margin - b.margin);
        const byMarginDesc = [...qualified].sort((a, b) => b.margin - a.margin);
        const toExtreme = (m: typeof qualified[number]): MarginExtreme => ({
          facility_id: m.row.facility_id,
          facility_name: m.row.facility_name ?? m.row.facility_id,
          account_name: m.row.account_id
            ? accountById[m.row.account_id]?.account_name ?? null
            : null,
          segment: m.segment,
          margin_pct: m.margin,
          monthly_rate: m.billingMonthly, // assignment-level, not f.monthly_rate
          arr: m.arr,
        });
        const marginExtremes: MarginExtremes = {
          lowest: byMarginAsc.slice(0, 5).map(toExtreme),
          highest: byMarginDesc.slice(0, 5).map(toExtreme),
        };

        if (cancelled) return;
        // Aggregates whose source queries were skipped surface as null (the
        // matching widgets are hidden, so nothing renders them anyway).
        const marginsFetched =
          needs.facilities && needs.accounts && needs.assignments;
        setData({
          loading: false,
          error: null,
          lastUpdated: new Date(),
          facilityHealth: needs.facilities ? facilityHealth : null,
          revenue: needs.facilities ? revenue : null,
          accountHealth:
            needs.accounts && needs.facilities && needs.feedback
              ? accountHealth
              : null,
          pipeline: needs.opportunities ? pipeline : null,
          alerts:
            needs.facilities && needs.feedback && needs.hiring ? alerts : null,
          geographic: needs.facilities ? geographic : null,
          portfolioSummary: marginsFetched ? portfolioSummary : null,
          marginDistribution: marginsFetched ? marginDistribution : null,
          marginExtremes: marginsFetched ? marginExtremes : null,
        });
      } catch (e) {
        if (cancelled) return;
        console.error('[dashboard] load failed', e);
        setData((prev) => ({
          ...prev,
          loading: false,
          error:
            errorMessage(e, 'Failed to load dashboard data.'),
        }));
      }
    }

    load();

    // Auto-refresh: 5-minute interval + on tab visibility regain. Both call
    // the same loader. We deliberately don't refresh on focus alone because
    // executives flick between windows constantly during a board prep — that
    // would hammer the API for no signal change.
    const interval = window.setInterval(() => {
      if (!cancelled) load();
    }, REFRESH_INTERVAL_MS);
    const onVisible = () => {
      if (!cancelled && document.visibilityState === 'visible') {
        load();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // needsKey (not the needs object) so refetches happen exactly when the
    // visible-widget domain set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick, needsKey, enabled]);

  return {
    ...data,
    refresh: () => setRefreshTick((n) => n + 1),
  };
}

// ----- Helpers --------------------------------------------------------------

function monthKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function countRecentFeedback(rows: FeedbackRow[], days: number): number {
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return rows.filter((fb) => {
    const t = Date.parse(fb.feedback_date);
    return Number.isFinite(t) && t >= cutoff;
  }).length;
}

// Order pipeline buckets the way leadership reads them: open work first
// (Pending → Active → Won), then within each stage by detail order.
const STAGE_ORDER: Record<string, number> = {
  Pending: 0,
  Active: 1,
  Won: 2,
  Unknown: 3,
};
const DETAIL_ORDER: Record<string, number> = {
  Onboarding: 0,
  Confirmation: 1,
  Pre_Invoicing: 2,
  QBO_Setup: 3,
  Executive_Review: 4,
  Won: 5,
};
function stageSort(a: OpportunityBucket, b: OpportunityBucket): number {
  const sa = STAGE_ORDER[a.stage] ?? 9;
  const sb = STAGE_ORDER[b.stage] ?? 9;
  if (sa !== sb) return sa - sb;
  const da = DETAIL_ORDER[a.stage_detail ?? ''] ?? 9;
  const db = DETAIL_ORDER[b.stage_detail ?? ''] ?? 9;
  return da - db;
}

// Segment classification for Bryan's portfolio view. The rules follow the
// dashboard README v2 (2026-05-12). Hospitals are all KC metro so the
// segment is just Hospital. ASC splits Active vs Pending. Dialysis splits
// by parent entity (DaVita KC, DaVita OOM, Fresenius, PureLife) using
// account name matching — this is the historical convention from the
// Master_File workbook and matches Bryan's preview HTML.
function segmentFor(
  f: FacilityRow,
  accountById: Record<string, AccountRow>,
): string {
  if (f.facility_type === 'Hospital') return 'Hospital';
  // WS5 2026-07-31: data value is Surgery_Center now; segment keys/labels
  // deliberately keep the familiar 'ASC' display term.
  if (f.facility_type === 'Surgery_Center') {
    return f.status === 'Pending' ? 'ASC-Pending' : 'ASC-Active';
  }
  if (f.facility_type === 'Dialysis') {
    const accountName = (f.account_id
      ? accountById[f.account_id]?.account_name ?? ''
      : ''
    ).toLowerCase();
    const market = (f.market_region ?? '').toLowerCase();
    if (accountName.startsWith('davita')) {
      return market.includes('kc') || market.includes('kansas city')
        ? 'Dialysis-DaVita-KC'
        : 'Dialysis-DaVita-OOM';
    }
    if (accountName.startsWith('fresenius')) return 'Dialysis-Fresenius';
    if (accountName.includes('pure life') || accountName.includes('purelife'))
      return 'Dialysis-PureLife';
    return 'Dialysis-Other';
  }
  if (f.facility_type === 'MOB') return 'MOB';
  return 'Other';
}
